import { action, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { handleSaveFile } from '@/utils/file';
import { AxiosErrorApi, AxiosRequestApi, AxiosResponseApi } from '@/types/axios';
import ApiService from '@/apis/ApiService';
import { t } from 'i18next';
import { ResponseData } from '@/types/http';
import { getServerMessage } from '@/utils/utils';

export interface CallApiOptions {
    handleErrorBySelf?: boolean,
    callbackError?: (response: AxiosResponseApi) => void,
    disableAlertError?: boolean,
    hideLoading?: boolean,
    isLoadingTable?: boolean
}

export default class ApiStore extends BaseStore {
    isLoading: boolean = false;
    isLoadingTable: boolean = false;
    requestCount: number = 0;
    requestCountTable: number = 0;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            isLoading: observable,
            isLoadingTable: observable,
            showLoading: action.bound,
            hideLoading: action.bound,
            setLoading: action.bound,
            handlerGeneralResponseSuccess: action.bound,
            handlerGeneralResponseError: action.bound,
            call: action.bound
        });
    }

    showLoading() {
        this.isLoading = true;
    }

    hideLoading() {
        this.isLoading = false;
    }

    setLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    handlerGeneralResponseSuccess = (response: AxiosResponseApi): ResponseData => {
        if (response && response.status === 200) {
            const type = response.headers['content-type'];
            const isExport = [
                'application/pdf',
                'application/zip',
                'text/csv; charset=utf-8',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
                ''
            ].includes(type);
            if (isExport) {
                handleSaveFile(response);
            }
            return response.data;
        }

        throw response;
    };

    handlerGeneralResponseError = (
        response?: AxiosResponseApi,
        { handleErrorBySelf, callbackError, disableAlertError }: Partial<CallApiOptions> = {}
    ): ResponseData => {
        if (handleErrorBySelf || !response) {
            throw response;
        }

        try {
            if (response.status === 401 || response.status === 403) {
                this.rootStore.authStore.clearAuthentication();
            } else if (response.status >= 500) {
                this.rootStore.modalStore.showAlertModal({
                    id: response.data.errorCode || 'API_RESPONSE_NO_NETWORK_OR_SERVER_ERROR',
                    type: 'error',
                    size: 'xl',
                    contentClassName: 'text-center',
                    content: t(`server_messages.${response.data.errorCode}`) || t('server_messages.API_RESPONSE_NO_NETWORK_OR_SERVER_ERROR')
                });
            } else if (response.status) {
                if (!disableAlertError) {
                    // const messError = getServerMessage(response.data.errorCode, response.data.errorParams, t);
                    // const errorId = (messError === `${t('server_messages.API_RESPONSE_NO_MESSAGE')}`) ? 'API_RESPONSE_NO_MESSAGE' : response.data.errorCode;
                    const messError = getServerMessage(response.data.errorCode, response.data.errorParams);
                    const errorId = `${t(messError.i18nKey)}` === `${t('server_messages.API_RESPONSE_NO_MESSAGE')}` ?
                        'API_RESPONSE_NO_MESSAGE' :
                        response.data.errorCode;

                    this.rootStore.modalStore.showAlertModal({
                        id: errorId,
                        type: 'error',
                        size: 'xl',
                        contentClassName: 'text-center',
                        content: messError
                    });
                }
                callbackError && callbackError(response);
            }
        } catch (error) {
            // this.rootStore.modalStore.showAlertModal({
            //     id: 'API_RESPONSE_NO_NETWORK_OR_SERVER_ERROR',
            //     type: 'error',
            //     contentClassName: 'text-center',
            //     content: t('server_messages.API_RESPONSE_NO_NETWORK_OR_SERVER_ERROR')
            // });
            const messError = getServerMessage('API_RESPONSE_NO_NETWORK_OR_SERVER_ERROR', {});
            this.rootStore.modalStore.showAlertModal({
                id: 'API_RESPONSE_NO_NETWORK_OR_SERVER_ERROR',
                type: 'error',
                size: 'xl',
                contentClassName: 'text-center',
                content: messError
            });
        } finally {
            throw response;
        }
    };

    call(
        contextRequest: ApiService,
        request: AxiosRequestApi,
        payload?: any,
        { handleErrorBySelf, callbackError, disableAlertError, hideLoading, isLoadingTable }: CallApiOptions = {}
    ): Promise<ResponseData> {
        this.requestCount += 1;
        if (!hideLoading) this.showLoading();
        if (isLoadingTable) {
            this.requestCountTable += 1;
            this.isLoadingTable = true;
        }
        return request.apply(contextRequest, [payload])
            .then(this.handlerGeneralResponseSuccess)
            .catch(async (error: AxiosErrorApi) => {
                const { response } = error;
                if (
                    response &&
                    response.data instanceof Blob &&
                    response.data.type === 'application/json'
                ) {
                    const text = await response.data.text();
                    try {
                        response.data = JSON.parse(text);
                    } catch (e) { }
                }
                return this.handlerGeneralResponseError(response, { handleErrorBySelf, callbackError, disableAlertError });
            })
            // .catch((response: AxiosErrorApi) => this.handlerGeneralResponseError(response.response, { handleErrorBySelf, callbackError, disableAlertError }))
            .finally(() => {
                this.requestCount -= 1;
                if (this.requestCount === 0) {
                    this.hideLoading();
                }
                if (isLoadingTable) {
                    this.requestCountTable -= 1;
                    if (this.requestCountTable === 0) {
                        this.isLoadingTable = false;
                    }
                }
            });
    }
}
